import type { Vec2, WaveParams } from "./types";

/**
 * Pure Gerstner-wave math. These functions are the source of truth for the wave
 * field; the WebGL fragment shader mirrors the same formulas so that the CPU
 * probe and the rendered surface agree.
 *
 * We visualize the *height field* `y(x, z, t)` sampled at each world point
 * (an Eulerian view), which is exactly what a top-down topographic map wants.
 */

/** Angular wavenumber `k = 2π / wavelength`. */
export function waveNumber(wavelength: number): number {
  return (2 * Math.PI) / wavelength;
}

/** Angular frequency `ω = speed · k`. */
export function angularFrequency(speed: number, wavelength: number): number {
  return speed * waveNumber(wavelength);
}

/** Phase `φ = k (D·P) − ω t` for a single wave at point `p` and time `t`. */
export function wavePhase(wave: WaveParams, p: Vec2, t: number): number {
  const k = waveNumber(wave.wavelength);
  const dirX = Math.cos(wave.direction);
  const dirZ = Math.sin(wave.direction);
  const omega = wave.speed * k;
  return k * (dirX * p.x + dirZ * p.z) - omega * t;
}

/**
 * Per-wave surface elevation shape as a function of phase.
 *
 * `steepness` in [0, 1] adds a phase-locked 2nd harmonic (a Stokes-wave term):
 * `sin φ − ½·steepness·cos 2φ`. This sharpens crests and flattens/widens troughs
 * along the wave's own direction — the real reason wind-driven waves look ridged —
 * while `steepness = 0` is a plain sine.
 */
function waveElevation(phase: number, steepness: number): number {
  return Math.sin(phase) - 0.5 * steepness * Math.cos(2 * phase);
}

/** Derivative of {@link waveElevation} with respect to phase. */
function waveElevationSlope(phase: number, steepness: number): number {
  return Math.cos(phase) + steepness * Math.sin(2 * phase);
}

/**
 * Summed vertical displacement (height) of all waves at `p`, time `t`.
 * `steepness` (0..1) sharpens crests via a Stokes 2nd harmonic — see
 * {@link waveElevation}.
 */
export function gerstnerHeight(
  waves: readonly WaveParams[],
  p: Vec2,
  t: number,
  steepness = 0,
): number {
  let y = 0;
  for (const wave of waves) {
    y += wave.amplitude * waveElevation(wavePhase(wave, p, t), steepness);
  }
  return y;
}

/**
 * Gradient of the height field, `(∂y/∂x, ∂y/∂z)`. With `steepness`, each wave's
 * slope follows {@link waveElevationSlope}.
 */
export function gerstnerGradient(
  waves: readonly WaveParams[],
  p: Vec2,
  t: number,
  steepness = 0,
): { dx: number; dz: number } {
  let dx = 0;
  let dz = 0;
  for (const wave of waves) {
    const k = waveNumber(wave.wavelength);
    const dirX = Math.cos(wave.direction);
    const dirZ = Math.sin(wave.direction);
    const c = wave.amplitude * k * waveElevationSlope(wavePhase(wave, p, t), steepness);
    dx += c * dirX;
    dz += c * dirZ;
  }
  return { dx, dz };
}

/** Unit surface normal `[x, y, z]` (y up) derived from the height gradient. */
export function gerstnerNormal(
  waves: readonly WaveParams[],
  p: Vec2,
  t: number,
  steepness = 0,
): [number, number, number] {
  const { dx, dz } = gerstnerGradient(waves, p, t, steepness);
  // Surface z = height(x, y); normal ∝ (-∂/∂x, 1, -∂/∂z).
  const nx = -dx;
  const ny = 1;
  const nz = -dz;
  const len = Math.hypot(nx, ny, nz) || 1;
  return [nx / len, ny / len, nz / len];
}

/**
 * Sum of the absolute amplitudes — the maximum possible |height|. Used to
 * normalize a height into [0, 1]. Never zero (clamped to a tiny epsilon) so
 * callers can divide safely.
 */
export function amplitudeSum(waves: readonly WaveParams[]): number {
  let sum = 0;
  for (const wave of waves) {
    sum += Math.abs(wave.amplitude);
  }
  return sum > 1e-6 ? sum : 1e-6;
}
